/**
 * Enumerations shared by the API and the PWA.
 *
 * `LeadStage` mirrors the Prisma enum of the same name (see docs/plan.md, Data
 * Model) — the build breaks if the two drift. The zod enum is the source of
 * truth for the *keys*; `LEAD_STAGE_DISPLAY` is the source of truth for the
 * user-visible copy. Stage colours are the web layer's own (`apps/web/src/lib/
 * stages.ts`), re-derived from the brand palette after the Industry theme was
 * removed. The key is never shown in the UI.
 */
import { z } from 'zod';
export const LeadStage = z.enum(['ENQUIRING', 'INTERESTED', 'SAMPLE_REQUESTED', 'RFQ', 'ORDER', 'LOST']);
/** Display copy for every `LeadStage`, keyed by the stage key. */
export const LEAD_STAGE_DISPLAY = {
    ENQUIRING: { label: 'Just Looking', hint: 'walked by' },
    INTERESTED: { label: 'Interested', hint: 'engaged' },
    SAMPLE_REQUESTED: { label: 'Sample Sent', hint: 'samples out' },
    RFQ: { label: 'Wants Quote', hint: 'quote sent' },
    ORDER: { label: 'Ordered', hint: 'confirmed' },
    LOST: { label: 'Dropped', hint: 'no go' }
};
/** How a buyer intends to pay. Selection is clearable in the UI — this schema does not require one. */
export const PaymentMode = z.enum(['CASH', 'UPI', 'CHEQUE', 'BANK_TRANSFER']);
/**
 * The unit a product's dimensions were MEASURED and stored in. The office works
 * mostly in inches, sometimes in cm; a product records whichever it was taken
 * with, and its value is stored exactly as entered (× 100, integer) — never
 * converted, because cm↔inch is lossy and no float may touch a measurement
 * (coding-standards.md, Money). Display shows the value with this unit's suffix.
 * A future buyer-facing "show everything in my unit" view would convert at the
 * display edge only, never on the stored value.
 */
export const DimensionUnit = z.enum(['INCH', 'CM']);
/** Display suffix for a `DimensionUnit`. */
export const DIMENSION_UNIT_LABEL = { INCH: 'in', CM: 'cm' };
/**
 * The one permanent "standing" event that off-show leads attach to — direct
 * sales enquiries and normal buyer meetings, captured without a tradeshow.
 *
 * `Lead.eventId` is required and lead refs are allocated per-event, so rather
 * than make `eventId` nullable (which would silently defeat the
 * `@@unique([eventId, ref])` guard, since Postgres treats NULLs as distinct),
 * every non-show lead attaches to this null-object event. The allocator, the
 * unique constraint, the offline mirror and the indexes stay unchanged.
 *
 * The row is created by the seed with this exact id (a plain string id is
 * safe — the allocator hashes it for its advisory lock and Dexie stores it as
 * text). The name is shared so the client can render the option even when the
 * events endpoint is unreachable (offline), and the seed reads it too, so the
 * two never drift.
 */
export const DIRECT_ENQUIRIES_EVENT_ID = 'direct-enquiries';
export const DIRECT_ENQUIRIES_EVENT_NAME = 'Direct Enquiries';
/** True for the standing "Direct Enquiries" bucket — recognised by id, never by name. */
export function isStandingEvent(eventId) {
    return eventId === DIRECT_ENQUIRIES_EVENT_ID;
}
//# sourceMappingURL=enums.js.map